import { isPullRequestUrl } from './agentDelegation';

/**
 * The delegated coding agent closes its final message with structured lines the local runner
 * reads back. Lives here rather than in the runner because the runner has no test suite, and
 * because the pull request check has to agree with the one every consuming surface uses.
 */
export const PR_URL_LINE_PREFIX = 'PR_URL=';
export const PR_URL_NONE = 'NONE';
export const COMMIT_MSG_LINE_PREFIX = 'COMMIT_MSG=';

/**
 * Wraps an optional pull request description, placed above the COMMIT_MSG=/PR_URL= lines (the
 * footer's own contract ends the message with those two and nothing after). GitHub rejects a
 * body over 65,536 characters; this stays an order of magnitude under that, generous for a
 * What/Why/Scope writeup and small enough to never need chunking through the same 24,000-char
 * relay limit the rest of this daemon's traffic is bound by.
 */
export const PR_BODY_START_TAG = '<pr_body>';
export const PR_BODY_END_TAG = '</pr_body>';
export const PR_BODY_MAX_CHARS = 8_000;

export const REVIEW_REPLY_LINE_PREFIX = 'REPLY=';
export const REVIEW_REPLY_PATTERN = new RegExp(
  `^${REVIEW_REPLY_LINE_PREFIX}(\\d+)\\s+(.+)$`,
);

export const AGENT_MESSAGE_MAX_CHARS = 4_000;
const TRUNCATION_SUFFIX = '...';
const FOOTER_LINE_PREFIXES: readonly string[] = [
  COMMIT_MSG_LINE_PREFIX,
  PR_URL_LINE_PREFIX,
  REVIEW_REPLY_LINE_PREFIX,
];
const EXCESS_BLANK_LINES_PATTERN = /\n{3,}/g;
const PARAGRAPH_BREAK = '\n\n';

const NONE_MARKER_PATTERN = /^NONE\b[\s:—–-]*/i;
const REASON_WRAPPER_PATTERN = /^\((.*)\)$/s;

export interface AgentJobReport {
  /** Set only when the agent reported a genuine pull request URL. */
  prUrl?: string;
  /** Whatever the agent put on the PR_URL line when it was not a pull request URL. */
  failureReason?: string;
  commitMessage?: string;
  /** A pull request description the agent wrote or rewrote, if any. */
  prBody?: string;
}

function findLastLineValue(
  lines: string[],
  prefix: string,
): string | undefined {
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const trimmed = lines[index].trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length).trim();
    }
  }
  return undefined;
}

/** "NONE (blocked on write permission — no files changed yet)" -> the parenthesised reason. */
function extractFailureReason(reportedValue: string): string | undefined {
  const withoutMarker = reportedValue.replace(NONE_MARKER_PATTERN, '').trim();
  const unwrapped =
    REASON_WRAPPER_PATTERN.exec(withoutMarker)?.[1]?.trim() ?? withoutMarker;
  return unwrapped || undefined;
}

function truncatePrBody(body: string): string {
  return body.length > PR_BODY_MAX_CHARS
    ? `${body.slice(0, PR_BODY_MAX_CHARS)}${TRUNCATION_SUFFIX}`
    : body;
}

interface LineRange {
  startIndex: number;
  endIndex: number;
}

/**
 * Takes the OUTERMOST tag pair — the first <pr_body> line in the message paired with the last
 * </pr_body> line after it — rather than the nearest matching pair. The agent's own description
 * can legitimately quote these tags as example text (this very feature is "a report that wraps
 * its PR body in <pr_body> tags"); taking the outermost pair folds a quoted example into the
 * body instead of letting it close the block early. An unmatched or reversed tag (no closing tag
 * after the opening one) is treated as no block at all, never as a partial one.
 */
function findPrBodyRange(lines: string[]): LineRange | undefined {
  const startIndex = lines.findIndex(
    (line) => line.trim() === PR_BODY_START_TAG,
  );
  if (startIndex === -1) {
    return undefined;
  }
  for (let index = lines.length - 1; index > startIndex; index -= 1) {
    if (lines[index].trim() === PR_BODY_END_TAG) {
      return { startIndex, endIndex: index };
    }
  }
  return undefined;
}

function extractPrBody(lines: string[]): string | undefined {
  const range = findPrBodyRange(lines);
  if (!range) {
    return undefined;
  }
  const body = lines
    .slice(range.startIndex + 1, range.endIndex)
    .join('\n')
    .trim();
  return body ? truncatePrBody(body) : undefined;
}

function isFooterLine(line: string): boolean {
  const trimmed = line.trim();
  return FOOTER_LINE_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
}

function withoutPrBodyBlock(lines: string[]): string[] {
  const range = findPrBodyRange(lines);
  return range
    ? [...lines.slice(0, range.startIndex), ...lines.slice(range.endIndex + 1)]
    : lines;
}

function capAgentMessage(message: string): string {
  return message.length > AGENT_MESSAGE_MAX_CHARS
    ? `${message.slice(0, AGENT_MESSAGE_MAX_CHARS - TRUNCATION_SUFFIX.length)}${TRUNCATION_SUFFIX}`
    : message;
}

/** The agent's final message as prose for the user: the structured footer the runner reads back
 *  (COMMIT_MSG=, PR_URL=, REPLY= lines and the <pr_body> block) is removed. */
export function extractAgentMessage(output: string): string | undefined {
  const message = withoutPrBodyBlock(output.split('\n'))
    .filter((line) => !isFooterLine(line))
    .join('\n')
    .replace(EXCESS_BLANK_LINES_PATTERN, PARAGRAPH_BREAK)
    .trim();
  return message ? capAgentMessage(message) : undefined;
}

/**
 * A value on the PR_URL line is a pull request URL or it is a failure — there is no third case.
 * The exact-match check this replaces accepted "NONE (blocked on write permission …)" as a URL,
 * marked the job DONE, and rendered the sentence as a link relative to the app origin.
 */
export function parseAgentJobReport(output: string): AgentJobReport {
  const lines = output.trim().split('\n');
  const commitMessage = findLastLineValue(lines, COMMIT_MSG_LINE_PREFIX);
  const reportedPrUrl = findLastLineValue(lines, PR_URL_LINE_PREFIX);
  const prBody = extractPrBody(lines);

  const report: AgentJobReport = {};
  if (commitMessage) {
    report.commitMessage = commitMessage;
  }
  if (prBody) {
    report.prBody = prBody;
  }
  if (reportedPrUrl === undefined) {
    return report;
  }
  if (isPullRequestUrl(reportedPrUrl)) {
    report.prUrl = reportedPrUrl.trim();
    return report;
  }
  report.failureReason = extractFailureReason(reportedPrUrl);
  return report;
}

function carriesReportFooter(text: string): boolean {
  return text
    .split('\n')
    .map((line) => line.trim())
    .some(
      (line) =>
        line.startsWith(COMMIT_MSG_LINE_PREFIX) ||
        line.startsWith(PR_URL_LINE_PREFIX),
    );
}

function findLast<T>(
  items: T[],
  predicate: (item: T) => boolean,
): T | undefined {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) {
      return items[index];
    }
  }
  return undefined;
}

/**
 * One claude process emits a `result` event per turn, and a subagent hand-back or a
 * task-notification starts another turn after the report — often an empty one. The report is the
 * last result carrying the COMMIT_MSG=/PR_URL= footer; PLAN has no footer, so it falls back to the
 * last non-empty result. A blank result never wins over a real one.
 */
export function selectAgentReportText(
  resultTexts: string[],
): string | undefined {
  const nonEmpty = resultTexts.filter((text) => text.trim());
  return findLast(nonEmpty, carriesReportFooter) ?? nonEmpty.at(-1);
}

/**
 * Never fails the job: a missing COMMIT_MSG line falls back to the job title verbatim.
 * The reported message is used as-is — the agent follows the target repository's own commit
 * convention, which this daemon has no business guessing or rewriting.
 */
export function resolveCommitMessage(
  reportedMessage: string | undefined,
  jobTitle: string,
): string {
  return reportedMessage?.trim() || jobTitle.trim();
}
